import { useSearchParams } from 'react-router-dom';
import { useEmpresas } from '@/api/queries/empresas';
import { useDashboard } from '@/api/queries/dashboard';
import { Input } from '@/components/ui/input';
import { EmpresaListVirtual } from '@/components/EmpresaListVirtual';
import { Skeleton } from '@/components/ui/skeleton';
import { Search } from 'lucide-react';
import { TAG_DEFS } from '@/lib/tags';

const TAGS: ReadonlyArray<readonly [string, string]> = [
  ['', 'todas as tags'],
  ...TAG_DEFS.map((d) => [d.slug, d.label] as const),
];

const SETORES = [
  ['', 'todos setores'],
  ['saude', 'saúde'],
  ['educacao', 'educação'],
  ['industria', 'indústria'],
  ['comercio', 'comércio'],
  ['turismo', 'turismo'],
  ['imobiliario', 'imobiliário'],
  ['automotivo', 'automotivo'],
  ['agro', 'agro'],
  ['servicos', 'serviços'],
  ['tecnologia', 'tecnologia'],
  ['financeiro', 'financeiro'],
  ['outro', 'outro'],
] as const;

const STATUS_FUNIL = [
  ['', 'todos estágios'],
  ['lista', 'lista'],
  ['abertura', 'abertura'],
  ['qualificacao', 'qualificação'],
  ['call_diagnostico', 'call diagnóstico'],
  ['diagnostico_pago', 'diagnóstico pago'],
  ['proposta', 'proposta'],
  ['objecao', 'objeção'],
  ['fechamento', 'fechamento'],
  ['onboarding', 'onboarding'],
  ['retencao', 'retenção'],
  ['perdido', 'perdido'],
  ['dead_onb', 'dead onb'],
] as const;

export function EmpresasPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const setor = searchParams.get('setor') ?? '';
  const statusFunil = searchParams.get('status_funil') ?? '';
  const tag = searchParams.get('tag') ?? '';
  const cidade = searchParams.get('cidade') ?? '';

  const { data: stats } = useDashboard();
  // Valores reais do banco (grafias mistas tipo AVARE/Avaré coexistem);
  // mantém a cidade da URL na lista mesmo que não esteja no top_cidades
  const cidades: Array<{ cidade: string; c?: number }> = stats?.top_cidades ?? [];
  const cidadeOptions = cidades.some((it) => it.cidade === cidade) || !cidade
    ? cidades
    : [{ cidade }, ...cidades];

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  }

  const { data, isLoading, error } = useEmpresas({
    q: q || undefined,
    setor: setor || undefined,
    status_funil: statusFunil || undefined,
    tag: tag || undefined,
    cidade: cidade || undefined,
    limit: 500,
  });

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between border-b border-border/70 pb-3">
        <div>
          <h1 className="text-[28px] font-semibold tracking-[-0.02em] leading-tight">Empresas</h1>
          <p className="mt-0.5 text-xs text-muted-foreground tabular">
            <span className="mono">{data?.length ?? 0}</span> no resultado
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60" strokeWidth={2.25} />
          <Input
            placeholder="buscar nome, razão social, cidade, notas..."
            value={q}
            onChange={(e) => setParam('q', e.target.value)}
            className="pl-8"
          />
        </div>
        <select
          value={setor}
          onChange={(e) => setParam('setor', e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {SETORES.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          value={cidade}
          onChange={(e) => setParam('cidade', e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">todas cidades</option>
          {cidadeOptions.map((it) => (
            <option key={it.cidade} value={it.cidade}>
              {it.cidade.toLowerCase()}{typeof it.c === 'number' ? ` · ${it.c}` : ''}
            </option>
          ))}
        </select>
        <select
          value={statusFunil}
          onChange={(e) => setParam('status_funil', e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {STATUS_FUNIL.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          value={tag}
          onChange={(e) => setParam('tag', e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {TAGS.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        {(q || setor || statusFunil || tag || cidade) && (
          <button
            type="button"
            onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
            className="h-9 rounded-md border border-border/70 px-3 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            limpar
          </button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">Erro carregando empresas.</p>}
      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : data ? (
        <EmpresaListVirtual items={data} />
      ) : null}
    </div>
  );
}
